import { useMemo, useState, type ReactNode } from 'react';
import { Link2, Timer, TimerOff, UserCheck } from 'lucide-react';
import {
  Button,
  InfoHint,
  Modal,
  SearchInput,
  SegmentedControl,
  Skeleton,
  toast,
} from '../../components/ui';
import { ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import {
  useAccountRoster,
  useLinkAccount,
  type AccountMember,
  type Person,
  type PersonType,
  type TimesheetCadence,
} from './api';
import { Avatar } from './components';

// ---------------------------------------------------------------------------
// Vocabulary — one spelling per cadence, used by every team screen
// ---------------------------------------------------------------------------

export const CADENCE_OPTIONS: Array<{ value: TimesheetCadence; label: string }> = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'NEVER', label: 'Never' },
];

/**
 * The cadence a person gets when tracking is first switched on. Contractors do
 * not work every week, so a weekly chase is noise for them; staff are weekly.
 */
export function defaultCadenceFor(type: PersonType): TimesheetCadence {
  return type === 'CONTRACTOR' ? 'MONTHLY' : 'WEEKLY';
}

/**
 * Whether submitting starts out required. Contractors do not have to submit
 * (Jesse, 2026-10-05) — they still CAN, it is just never late.
 */
export function defaultRequiredFor(type: PersonType): boolean {
  return type !== 'CONTRACTOR';
}

/** "submits weekly" / "never submits" — the short label, never a sentence. */
export function cadencePhrase(cadence: TimesheetCadence): string {
  switch (cadence) {
    case 'DAILY':
      return 'submits daily';
    case 'WEEKLY':
      return 'submits weekly';
    case 'MONTHLY':
      return 'submits monthly';
    default:
      return 'never submits';
  }
}

/**
 * The whole policy as one chip label.
 *
 * ⚠ Pass `hasSolidAccount` where it is known. The server refuses to TURN ON
 * tracking without an account, but Harvest-era rows arrived already tracking
 * and unlinked — and they are still chased on their cadence while no timer can
 * sign in as them. "tracks time · submits weekly" over such a row claims a
 * timer that does not exist; the label has to say what is missing.
 */
export function timePolicyLabel(
  person: Pick<Person, 'tracksTime' | 'timesheetCadence'> & {
    hasSolidAccount?: boolean;
    timesheetRequired?: boolean;
  },
): string {
  if (!person.tracksTime) return 'no time tracking';
  const optional = person.timesheetRequired === false && person.timesheetCadence !== 'NEVER';
  const policy = `tracks time · ${cadencePhrase(person.timesheetCadence)}${optional ? ' (optional)' : ''}`;
  return person.hasSolidAccount === false ? `${policy} · no account` : policy;
}

// ---------------------------------------------------------------------------
// The form control — used by New person AND Edit person
// ---------------------------------------------------------------------------

export interface TimePolicyValue {
  tracksTime: boolean;
  timesheetCadence: TimesheetCadence;
  /** Submitting is required — off = optional, never late or reminded. */
  timesheetRequired: boolean;
}

/**
 * "Tracks time" + "Timesheet submissions".
 *
 * The frequency is REQUIRED when tracking is on and meaningless when it is off,
 * so it is shown only while the box is ticked; unticking sends `NEVER`. The
 * first tick picks the default for the person's type — no extra step for the
 * common case.
 *
 * Without a Solid account the box cannot be ticked: the timer signs in as the
 * person, and the server refuses tracking for an account-less row. The reason
 * is one line, and the fix (`accountAction`) sits beside it.
 */
export function TimePolicyFields({
  value,
  onChange,
  type,
  hasAccount,
  accountAction,
  className,
}: {
  value: TimePolicyValue;
  onChange: (next: TimePolicyValue) => void;
  type: PersonType;
  hasAccount: boolean;
  accountAction?: ReactNode;
  className?: string;
}) {
  const disabled = !hasAccount && !value.tracksTime;
  return (
    <div className={cn('rounded-lg border border-grey-200 bg-grey-50 p-3', className)}>
      <label
        className={cn(
          'flex items-start gap-3',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-secondary disabled:cursor-not-allowed"
          checked={value.tracksTime}
          disabled={disabled}
          onChange={(e) => {
            const on = e.target.checked;
            const first = on && !value.tracksTime;
            onChange({
              tracksTime: on,
              timesheetCadence: on
                ? value.timesheetCadence === 'NEVER'
                  ? defaultCadenceFor(type)
                  : value.timesheetCadence
                : 'NEVER',
              timesheetRequired: first ? defaultRequiredFor(type) : value.timesheetRequired,
            });
          }}
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1 text-body-sm font-semibold text-secondary">
            Tracks time
            <InfoHint label="Tracks time" size="sm">
              <p>
                Shows the timer in the Solid app bar, in every app, and in the hub. Turn it off for
                people whose job does not involve logging hours — their history stays either way.
              </p>
              <p className="mt-2">
                The timer signs in as the person, so tracking needs an accounts.soliddigital.com
                account linked to them.
              </p>
            </InfoHint>
          </span>
          <span className="mt-0.5 block text-caption text-grey-500">
            {disabled
              ? 'Needs a Solid account — link one first.'
              : 'Shows the timer in the Solid app bar.'}
          </span>
        </span>
      </label>
      {disabled && accountAction && <div className="mt-2 pl-7">{accountAction}</div>}
      {value.tracksTime && (
        <div className="mt-3 pl-7">
          <div className="mb-1.5 flex items-center gap-1">
            <span className="label !mb-0">Timesheet submissions</span>
            <InfoHint label="Timesheet submissions" size="sm" width={320}>
              <p>
                How often a timesheet is expected: each <strong>day</strong>, each{' '}
                <strong>week</strong> or each <strong>month</strong>. That period is what they
                submit, what an approver signs off, and what the reminder email chases.
              </p>
              <p className="mt-2">
                <strong>Never</strong> means they log hours but nobody chases a sheet — an owner who
                tracks the odd hour, say.
              </p>
              <p className="mt-2">
                New trackers start on weekly for staff and monthly for contractors.
              </p>
            </InfoHint>
          </div>
          <SegmentedControl
            ariaLabel="Timesheet submissions"
            value={value.timesheetCadence}
            onChange={(timesheetCadence) => onChange({ ...value, timesheetCadence })}
            options={CADENCE_OPTIONS}
          />
          {value.timesheetCadence !== 'NEVER' && (
            <label className="mt-2 flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-secondary"
                checked={value.timesheetRequired}
                onChange={(e) => onChange({ ...value, timesheetRequired: e.target.checked })}
              />
              <span className="min-w-0">
                <span className="text-body-sm font-semibold text-secondary">Required</span>
                <span className="mt-0.5 block text-caption text-grey-500">
                  {value.timesheetRequired
                    ? 'A period not sent in is late — listed on Approvals and reminded.'
                    : 'Optional — they can submit, and nothing is chased if they don’t.'}
                </span>
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The accounts roster — the only place a person comes from
// ---------------------------------------------------------------------------

function matches(member: AccountMember, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return member.name.toLowerCase().includes(q) || member.email.toLowerCase().includes(q);
}

/** Loading / unreachable states shared by both pickers. */
function RosterFrame({
  roster,
  children,
}: {
  roster: ReturnType<typeof useAccountRoster>;
  children: (members: AccountMember[]) => ReactNode;
}) {
  if (roster.isPending) {
    return (
      <div className="space-y-2" aria-busy>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }
  if (roster.isError) {
    return (
      <div role="alert" className="rounded-lg border border-status-late-border bg-status-late-bg p-3 text-body-sm">
        <p className="font-semibold text-status-late">Couldn&rsquo;t load the accounts roster.</p>
        <p className="mt-1 text-secondary-800">
          {roster.error instanceof Error ? roster.error.message : 'Please try again.'}
        </p>
        <Button size="sm" variant="outline" className="mt-2" onClick={() => void roster.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!roster.data.ok) {
    return (
      <div role="alert" className="rounded-lg border border-status-partial-border bg-status-partial-bg p-3 text-body-sm">
        <p className="font-semibold text-secondary">accounts.soliddigital.com isn&rsquo;t reachable.</p>
        <p className="mt-1 text-secondary-800">
          {roster.data.reason ?? 'The roster could not be read.'} People can only be added from an
          account, so there is nothing to add until it answers.
        </p>
        <Button size="sm" variant="outline" className="mt-2" onClick={() => void roster.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return <>{children(roster.data.members)}</>;
}

function MemberIdentity({ member }: { member: AccountMember }) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar name={member.name} />
      <span className="min-w-0">
        <span className="block truncate font-semibold text-secondary">{member.name}</span>
        <span className="block truncate text-caption text-grey-500">{member.email}</span>
      </span>
    </span>
  );
}

/**
 * Step one of New person: pick the account. Members already in financials are
 * shown, greyed, so the list matches accounts one to one; a member whose email
 * belongs to an account-less person offers "Link", never "Add".
 */
export function AccountPicker({
  enabled,
  onPick,
  onLinked,
}: {
  enabled: boolean;
  onPick: (member: AccountMember) => void;
  onLinked: (person: Person) => void;
}) {
  const roster = useAccountRoster(enabled);
  const link = useLinkAccount();
  const [query, setQuery] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const doLink = async (member: AccountMember) => {
    if (!member.matchPersonId) return;
    setLinkError(null);
    try {
      const person = await link.mutateAsync({ id: member.matchPersonId, authUserId: member.userId });
      toast.success(`${person.name} is linked to their Solid account.`);
      onLinked(person);
    } catch (err) {
      setLinkError(err instanceof ApiError ? err.message : 'Could not link — please try again.');
    }
  };

  return (
    <RosterFrame roster={roster}>
      {(members) => {
        const order = (m: AccountMember) => (m.personId ? 2 : m.matchPersonId ? 1 : 0);
        const shown = members
          .filter((m) => matches(m, query))
          .sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
        const addable = members.filter((m) => !m.personId && !m.matchPersonId).length;
        const linkable = members.filter((m) => !m.personId && m.matchPersonId).length;
        return (
          <div>
            <SearchInput
              searches="accounts"
              value={query}
              onChange={setQuery}
              shortcut={false}
              className="mb-2"
            />
            <p className="mb-2 flex flex-wrap gap-x-3 text-caption text-grey-500">
              <span>
                <span className="num font-semibold text-secondary">{addable}</span> to add
              </span>
              {linkable > 0 && (
                <span>
                  <span className="num font-semibold text-secondary">{linkable}</span> to link to an
                  existing person
                </span>
              )}
              <span>
                <span className="num font-semibold text-secondary">
                  {members.length - addable - linkable}
                </span>{' '}
                already added
              </span>
            </p>
            {linkError && (
              <p className="mb-2 text-body-sm font-medium text-error" role="alert">
                {linkError}
              </p>
            )}
            {shown.length === 0 ? (
              <p className="rounded-lg border border-dashed border-grey-200 p-4 text-center text-body-sm text-grey-500">
                {members.length === 0
                  ? 'Nobody is in this organization in accounts.soliddigital.com yet.'
                  : 'No account matches that search.'}
              </p>
            ) : (
              <ul className="max-h-[22rem] divide-y divide-grey-100 overflow-y-auto rounded-lg border border-grey-200">
                {shown.map((m) => (
                  <li key={m.userId}>
                    {m.personId ? (
                      <div className="flex items-center justify-between gap-3 px-3 py-2.5 opacity-60">
                        <MemberIdentity member={m} />
                        <span className="inline-flex shrink-0 items-center gap-1 text-caption font-semibold text-grey-500">
                          <UserCheck className="h-3.5 w-3.5" aria-hidden />
                          {m.personActive === false ? 'Already added · archived' : 'Already added'}
                        </span>
                      </div>
                    ) : m.matchPersonId ? (
                      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                        <MemberIdentity member={m} />
                        <Button
                          size="sm"
                          variant="outline"
                          loading={link.isPending && link.variables?.authUserId === m.userId}
                          disabled={link.isPending}
                          onClick={() => void doLink(m)}
                        >
                          <Link2 className="h-4 w-4" aria-hidden /> Link to {m.matchPersonName}
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onPick(m)}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-grey-50 focus:outline-none focus-visible:bg-grey-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
                      >
                        <MemberIdentity member={m} />
                        <span className="shrink-0 text-body-sm font-semibold text-accent-700">
                          Add
                        </span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }}
    </RosterFrame>
  );
}

/**
 * Link an existing account-less person to an account. Only unbound members are
 * offered; the one whose email matches this person is listed first.
 */
export function LinkAccountList({
  person,
  enabled,
  onLinked,
}: {
  person: Pick<Person, 'id' | 'name'>;
  enabled: boolean;
  onLinked: (person: Person) => void;
}) {
  const roster = useAccountRoster(enabled);
  const link = useLinkAccount();
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const doLink = async (member: AccountMember) => {
    setError(null);
    try {
      const saved = await link.mutateAsync({ id: person.id, authUserId: member.userId });
      toast.success(`${saved.name} is linked to ${member.email}.`);
      onLinked(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not link — please try again.');
    }
  };

  return (
    <RosterFrame roster={roster}>
      {(members) => {
        const free = members
          .filter((m) => !m.personId && matches(m, query))
          .sort(
            (a, b) =>
              Number(b.matchPersonId === person.id) - Number(a.matchPersonId === person.id) ||
              a.name.localeCompare(b.name),
          );
        return (
          <div>
            <SearchInput
              searches="accounts"
              value={query}
              onChange={setQuery}
              shortcut={false}
              className="mb-2"
            />
            {error && (
              <p className="mb-2 text-body-sm font-medium text-error" role="alert">
                {error}
              </p>
            )}
            {free.length === 0 ? (
              <p className="rounded-lg border border-dashed border-grey-200 p-4 text-center text-body-sm text-grey-500">
                {query ? 'No unlinked account matches that search.' : 'Every account is already linked to somebody.'}
              </p>
            ) : (
              <ul className="max-h-[18rem] divide-y divide-grey-100 overflow-y-auto rounded-lg border border-grey-200 bg-white">
                {free.map((m) => (
                  <li key={m.userId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                    <MemberIdentity member={m} />
                    <span className="flex shrink-0 items-center gap-2">
                      {m.matchPersonId === person.id && (
                        <span className="text-caption font-semibold text-accent-700">same email</span>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        loading={link.isPending && link.variables?.authUserId === m.userId}
                        disabled={link.isPending}
                        onClick={() => void doLink(m)}
                      >
                        <Link2 className="h-4 w-4" aria-hidden /> Link
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }}
    </RosterFrame>
  );
}

export function LinkAccountModal({
  person,
  open,
  onOpenChange,
}: {
  person: Pick<Person, 'id' | 'name'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Link ${person.name} to an account`}
      description="Pick their accounts.soliddigital.com account. Their hours and history stay as they are."
      size="md"
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
          Cancel
        </Button>
      }
    >
      <LinkAccountList person={person} enabled={open} onLinked={() => onOpenChange(false)} />
    </Modal>
  );
}

/** The glyph for a policy: a running clock or a stopped one. */
export function TimePolicyGlyph({ tracksTime }: { tracksTime: boolean }) {
  return tracksTime ? (
    <Timer className="h-3.5 w-3.5" aria-hidden />
  ) : (
    <TimerOff className="h-3.5 w-3.5" aria-hidden />
  );
}

/** Stable memo helper for callers that sort by cadence. */
export function useCadenceCounts(people: Array<Pick<Person, 'tracksTime' | 'timesheetCadence'>>) {
  return useMemo(() => {
    const counts: Record<TimesheetCadence, number> = { DAILY: 0, WEEKLY: 0, MONTHLY: 0, NEVER: 0 };
    for (const p of people) if (p.tracksTime) counts[p.timesheetCadence] += 1;
    return counts;
  }, [people]);
}
